import { useMemo } from 'react';

const KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'fbc', 'fbp'] as const;
type AdKey = typeof KEYS[number];
type AdParams = Partial<Record<AdKey, string>>;

const SS_PREFIX = 'ad_param_';
const LS_KEY = 'ad_param_pack';
const LS_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function getParamsFromUrl(): AdParams {
  const p = new URLSearchParams(window.location.search);
  const out: AdParams = {};
  KEYS.forEach((k) => {
    const v = p.get(k);
    if (v) out[k] = v;
  });
  return out;
}

function getParamsFromSession(): AdParams {
  const out: AdParams = {};
  try {
    KEYS.forEach((k) => {
      const v = sessionStorage.getItem(SS_PREFIX + k);
      if (v) out[k] = v;
    });
  } catch (e) { /* noop */ }
  return out;
}

function saveParamsToSession(obj: AdParams): void {
  try {
    (Object.keys(obj) as AdKey[]).forEach((k) => {
      if (obj[k]) sessionStorage.setItem(SS_PREFIX + k, obj[k] as string);
    });
  } catch (e) { /* noop */ }
}

function saveParamsToLocalIfNeeded(obj: AdParams): void {
  try {
    if (localStorage.getItem(LS_KEY)) return;
    const pack: { ts: number; params: Record<string, string> } = { ts: Date.now(), params: {} };
    KEYS.forEach((k) => {
      if (obj[k]) pack.params[k] = obj[k] as string;
    });
    if (!Object.keys(pack.params).length) return;
    localStorage.setItem(LS_KEY, JSON.stringify(pack));
  } catch (e) { /* noop */ }
}

function getParamsFromLocalIfValid(): AdParams {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const pack = JSON.parse(raw) as { ts: number; params: Record<string, string> };
    if (!pack || !pack.ts || !pack.params) return {};
    if (Date.now() - pack.ts > LS_TTL_MS) {
      localStorage.removeItem(LS_KEY);
      return {};
    }
    return pack.params as AdParams;
  } catch (e) {
    return {};
  }
}

export function useAdParams() {
  const appendParams = useMemo(() => {
    const fromUrl = getParamsFromUrl();
    if (Object.keys(fromUrl).length > 0) {
      saveParamsToSession(fromUrl);
      saveParamsToLocalIfNeeded(fromUrl);
    }

    const fromSS = getParamsFromSession();
    const fromLS = getParamsFromLocalIfValid();

    const params: Record<string, string> = {};
    KEYS.forEach((k) => {
      const v = fromUrl[k] ?? fromSS[k] ?? fromLS[k];
      if (v) params[k] = v;
    });

    return (href: string): string => {
      if (!Object.keys(params).length) return href;
      try {
        const url = new URL(href, window.location.href);
        Object.entries(params).forEach(([k, v]) => {
          url.searchParams.set(k, v);
        });
        return url.toString();
      } catch (e) {
        return href;
      }
    };
  }, []);

  return { appendParams };
}
